import { useEffect, useRef, useState } from 'react';
import { AnimatePresence, motion } from 'motion/react';
import {
  api,
  type CaminoPlanta,
  type MuellePlanta,
  type NavePlanta,
  type SectorPlanta,
  type RackPlanta,
} from '../../api';
import { mensajeDeError, useApi, useEscape } from '../../lib/useApi';
import { useEstadoGuardado } from '../../lib/persistencia';
import { Cargando, Encabezado, Icono, Numero, PanelError, useConfirmar, type Pregunta } from '../../components/ui';
import { altoMinimo, aviso, botonSecundario, c, chip, colorCamino, colorSector, mono, nota, tarjeta } from '../../theme';
import { codigosDeUbicacion, svgEtiquetaUbicacion } from '../../lib/etiquetasUbicacion';
import { imprimirEtiquetas } from '../../lib/imprimir';
import { aparecer, desplegar, fila, pop, resorte, revelarDesde } from '../../components/movimiento';
import { useEsCelular } from '../../lib/useMedia';
import {
  PASO_M,
  aCoordenadasDeNave,
  ajustar,
  ajustarASegmento,
  claveDeLado,
  claveDePared,
  engancharTramoABordes,
  LADOS,
  ladoDeNave,
  muroMasCercano,
  naveEnElPunto,
  rectanguloDelTrazo,
  type Dibujo,
  type Lado,
  type MuroPlano,
  type Tipo,
} from './arrastre';
import PlanoEditable from './PlanoEditable';
import {
  EN_EL_PREDIO,
  FormularioAbertura,
  FormularioCamino,
  FormularioMuelle,
  FormularioNave,
  FormularioPared,
  FormularioPredio,
  FormularioRack,
  FormularioSector,
  PanelRack,
  Plegable,
  type ValoresRack,
} from './formularios';

/**
 * Invariante de coordenadas del arrastre, que es lo más fácil de romper de
 * todo el editor: `dxM`/`dyM` y el `preview` están **siempre en coordenadas
 * del predio**, porque de ahí salen (`aMetros` devuelve un punto del SVG, y
 * el SVG encuadra el predio). Lo guardado en la base, en cambio, es relativo
 * al rincón de su nave. Por eso al agarrar algo hay que trasladar la posición
 * guardada antes de restarla, y la vuelta a coordenadas de nave pasa **una
 * sola vez**: al guardar, en `alSoltar`.
 */
type Arrastre = { tipo: Tipo; id: string; dxM: number; dyM: number };

/** Cómo se lee cada modo de dibujo en el pie del plano. La abertura no entra
 * acá: no se «marca», se hace clic, así que su pie de página es aparte. */
const QUE_SE_DIBUJA: Record<Exclude<Dibujo, 'abertura'>, string> = {
  sector: 'del sector',
  camino: 'del camino',
  nave: 'de la nave',
  pared: 'de la pared',
  rack: 'del rack',
};

/**
 * Editor de la planta: se dibujan las naves del predio a escala y se acomodan
 * los racks adentro. El SVG usa **metros como unidades** (viewBox = medidas
 * del predio), así que las coordenadas del dibujo y las de la base son las
 * mismas salvo por el origen de cada nave, que se suma al dibujar.
 */
export default function Planta() {
  const { datos, cargando, error, recargar } = useApi(() => api.planta(), []);
  /**
   * Qué se estaba editando: sobrevive al F5 (ver `lib/persistencia.ts`). En
   * este editor recargar dolía más que en ninguna otra pantalla, porque volver
   * a lo que se estaba tocando es buscarlo de nuevo en el dibujo. Un id que ya
   * no existe se resuelve solo: más abajo se busca contra los datos de la
   * planta y, si no está, queda en null.
   */
  const [seleccion, setSeleccion] = useEstadoGuardado<string | null>('planta.seleccion', null);
  /**
   * El aviso flotante. `deshacer` lo trae sólo el que sale de un arrastre: es
   * lo único del editor que se guarda sin un botón de por medio, así que es lo
   * único que necesita vuelta atrás. Un nivel alcanza: el aviso siguiente lo
   * reemplaza.
   */
  const [avisoTexto, setAvisoTexto] = useState<{
    texto: string;
    ok: boolean;
    deshacer?: () => Promise<unknown>;
  } | null>(null);
  /**
   * Qué sección del panel está abierta. Antes eran nueve tarjetas apiladas y
   * para llegar a «Caminos» había que bajar media pantalla; con una abierta por
   * vez el panel entra entero. Sobrevive al F5 igual que la selección.
   */
  const [seccion, setSeccion] = useEstadoGuardado<string | null>('planta.seccion', 'Naves');
  /**
   * Cuántos pedidos rechazó el backend hasta ahora. Entra en la `key` de los
   * campos `defaultValue` + `onBlur` de nave, pared y abertura: son inputs no
   * controlados que sólo releen su valor al montarse, así que un 400/409 los
   * dejaba mostrando lo que el operario tipeó -y no lo que de verdad quedó
   * guardado- hasta que se recargaba la página a mano. Incrementarlo fuerza
   * el remontaje de esas filas después de cada rechazo.
   */
  const [fallas, setFallas] = useState(0);
  const [arrastre, setArrastre] = useState<Arrastre | null>(null);
  const [preview, setPreview] = useState<{ tipo: Tipo; id: string; x: number; y: number } | null>(null);
  const svg = useRef<SVGSVGElement>(null);
  const celular = useEsCelular();

  // Formularios
  const [predio, setPredio] = useState({ nombre: '' });
  // Sin ancho ni largo: la nave nueva toma las medidas del rectángulo que se
  // dibuja sobre el plano. Sólo el nombre y el alto se cargan a mano.
  const [nuevaNave, setNuevaNave] = useState({ nombre: '', alto: '8' });
  const [nuevoSector, setNuevoSector] = useState({ id: '', nombre: '', uso: '', nave: '' });
  const [nuevoRack, setNuevoRack] = useState<ValoresRack>({
    sector: '',
    numero: '1',
    columnas: '10',
    niveles: '3',
    cantidad: '1',
    separacion: '3',
    tipo: 'rack',
  });
  const [nuevoMuelle, setNuevoMuelle] = useState({ id: '', ancho: '3.5', largo: '1.2', nave: '' });
  const [nuevoCamino, setNuevoCamino] = useState({
    nombre: '',
    tipo: 'circulacion',
    ancho: '3',
    largo: '12',
    nave: '',
  });
  // Sin espesor: no se edita desde acá, sale del valor por defecto del
  // backend. El alto arranca vacío, que es «hasta el techo» — el caso más
  // común para un tabique interior.
  const [nuevaPared, setNuevaPared] = useState({ material: '', alto: '' });
  // Sin ancho, alto ni antepecho: los trae el tipo elegido. El nombre es
  // opcional, así que puede quedar vacío al colocarla.
  const [nuevaAbertura, setNuevaAbertura] = useState({ tipo: '', nombre: '' });
  // Modo dibujo: en vez de crear el camino y después acomodarlo, se marca el
  // rectángulo sobre el plano y queda hecho ahí.
  const [dibujando, setDibujando] = useState<Dibujo | null>(null);
  const [trazo, setTrazo] = useState<{ x0: number; y0: number; x1: number; y1: number } | null>(null);

  // El aviso flota abajo y se va solo: antes quedaba arriba de todo, fuera de
  // vista, y el que estaba trabajando en el plano no se enteraba del error.
  // El error no se va solo: queda hasta que se lo cierra, que si no se pierde
  // sin leer.
  useEffect(() => {
    if (!avisoTexto?.ok) return;
    const t = setTimeout(() => setAvisoTexto(null), 5000);
    return () => clearTimeout(t);
  }, [avisoTexto]);

  // Esc sale del modo dibujo. Va en el documento y no en el SVG porque el foco
  // suele quedar en el botón que lo activó, o en ningún lado.
  useEscape(!!dibujando, () => {
    setDibujando(null);
    setTrazo(null);
  });

  useEffect(() => {
    if (!datos) return;
    setPredio({ nombre: datos.predio.nombre });
    // Lo que se crea sin decir dónde va a la primera nave, que es lo que hacía
    // cuando había una sola. El `||` deja lo que el operario ya eligió...
    const primera = String(datos.naves[0]?.id ?? '');
    // ...pero sólo si esa nave sigue existiendo. Si el operario eligió «Nave
    // 2» y alguien la borró después, el id viejo sigue siendo truthy y el
    // `||` no lo tocaba: el `<select>` mostraba la primera opción por falta
    // de match, pero al crear se seguía mandando el id de la nave borrada, y
    // el backend lo rechazaba con «Esa nave no existe.» sobre una nave que la
    // pantalla ya no ofrecía.
    const naveExiste = (id: string) => datos.naves.some((n) => String(n.id) === id);
    setNuevoSector((s) => ({ ...s, nave: naveExiste(s.nave) ? s.nave : primera }));
    setNuevoMuelle((m) => ({ ...m, nave: naveExiste(m.nave) ? m.nave : primera }));
    // El camino va aparte: «en el predio» es una opción válida suya, y con el
    // `||` de arriba se la comería, porque este efecto corre después de cada
    // creación (accion() siempre recarga) y no sólo al abrir la página. El
    // operario elegía «En el predio», creaba el pasillo de la playa, y el
    // segundo se guardaba adentro de la nave 1 sin aviso. Sólo se completa
    // cuando el campo está genuinamente sin inicializar, o cuando apuntaba a
    // una nave que ya no existe.
    setNuevoCamino((k) => ({
      ...k,
      nave: k.nave === '' || (k.nave !== EN_EL_PREDIO && !naveExiste(k.nave)) ? primera : k.nave,
    }));
    setNuevoRack((r) => ({ ...r, sector: r.sector || datos.sectores[0]?.id || '' }));
    setNuevaPared((p) => ({ ...p, material: p.material || datos.paredPorDefecto.material }));
    setNuevaAbertura((a) => ({ ...a, tipo: a.tipo || datos.tiposDeAbertura[0]?.id || '' }));
  }, [datos]);

  /**
   * El único lugar que decide qué pasa después de un pedido: si salió bien,
   * el aviso de éxito y la limpieza que haya pedido el llamador (`alExito`);
   * si no, el aviso de error y un remonte de los campos no controlados que
   * puedan haber quedado mintiendo (`fallas`). Antes cada llamador hacía su
   * propia limpieza con `.then()`, que corre pase lo que pase porque `accion`
   * atrapa el error: un 400 al crear un sector vaciaba igual sus tres campos,
   * y uno al crear un rack le hacía saltar el número igual.
   */
  async function accion(
    fn: () => Promise<unknown>,
    exito?: string,
    alExito?: () => void,
    deshacer?: () => Promise<unknown>,
  ) {
    setAvisoTexto(null);
    try {
      await fn();
      if (exito) setAvisoTexto({ texto: exito, ok: true, deshacer });
      alExito?.();
      recargar();
    } catch (err) {
      setAvisoTexto({ texto: mensajeDeError(err), ok: false });
      setFallas((f) => f + 1);
      recargar();
    }
  }

  const [confirmar, dialogo] = useConfirmar();
  /** Un borrado: primero se pregunta, y sólo si confirma pasa por `accion`. */
  async function borrar(p: Pregunta, fn: () => Promise<unknown>, exito: string, alExito?: () => void) {
    if (await confirmar(p)) await accion(fn, exito, alExito);
  }

  /** Convierte un punto de pantalla a metros dentro del plano. */
  function aMetros(e: { clientX: number; clientY: number }) {
    const el = svg.current;
    if (!el) return null;
    const p = el.createSVGPoint();
    p.x = e.clientX;
    p.y = e.clientY;
    const m = el.getScreenCTM();
    if (!m) return null;
    const q = p.matrixTransform(m.inverse());
    return { x: q.x, y: q.y };
  }

  /** El origen de la nave de algo, para pasar lo guardado a coordenadas del predio. */
  function origenDeNave(naveId: number | null): { x: number; y: number } {
    const n = datos?.naves.find((v) => v.id === naveId);
    return n ? { x: n.x, y: n.y } : { x: 0, y: 0 };
  }

  /** La nave de un rack es la de su sector: el rack no la guarda. */
  function naveDeRack(rackId: string): number | null {
    const rack = datos?.racks.find((r) => r.id === rackId);
    return datos?.sectores.find((s) => s.id === rack?.sector)?.nave ?? null;
  }

  /**
   * Contra qué se centra lo que se ubica desde el panel: contra su nave, o
   * contra el predio entero cuando no está en ninguna (la playa de maniobras).
   */
  function medidasDondeVa(naveId: number | null): { ancho: number; largo: number } {
    const n = datos?.naves.find((v) => v.id === naveId);
    if (n) return { ancho: n.ancho, largo: n.largo };
    return { ancho: datos?.predio.ancho ?? 0, largo: datos?.predio.largo ?? 0 };
  }

  function alBajar(e: React.PointerEvent, r: RackPlanta) {
    setSeleccion(r.id);
    if (!r.ubicado || !r.huella) return;
    const p = aMetros(e);
    if (!p) return;
    e.currentTarget.setPointerCapture(e.pointerId);
    const o = origenDeNave(naveDeRack(r.id));
    setArrastre({ tipo: 'rack', id: r.id, dxM: p.x - (r.huella.x + o.x), dyM: p.y - (r.huella.y + o.y) });
  }

  // Tocar algo en el plano abre su sección del panel, que es donde se edita.
  function alBajarMuelle(e: React.PointerEvent, m: MuellePlanta) {
    setSeccion('Muelles');
    if (!m.ubicado) return;
    const p = aMetros(e);
    if (!p) return;
    e.currentTarget.setPointerCapture(e.pointerId);
    const o = origenDeNave(m.nave);
    setArrastre({ tipo: 'muelle', id: m.id, dxM: p.x - (m.x! + o.x), dyM: p.y - (m.y! + o.y) });
  }

  function alBajarSector(e: React.PointerEvent, sector: SectorPlanta) {
    setSeccion('Sectores');
    if (!sector.dibujada || !sector.area) return;
    const p = aMetros(e);
    if (!p) return;
    e.currentTarget.setPointerCapture(e.pointerId);
    setSeleccion(null);
    const o = origenDeNave(sector.nave);
    setArrastre({
      tipo: 'sector',
      id: sector.id,
      dxM: p.x - (sector.area.x + o.x),
      dyM: p.y - (sector.area.y + o.y),
    });
  }

  function alBajarCamino(e: React.PointerEvent, k: CaminoPlanta) {
    setSeccion('Caminos y zonas');
    if (!k.ubicado) return;
    const p = aMetros(e);
    if (!p) return;
    e.currentTarget.setPointerCapture(e.pointerId);
    const o = origenDeNave(k.nave);
    setArrastre({ tipo: 'camino', id: String(k.id), dxM: p.x - (k.x! + o.x), dyM: p.y - (k.y! + o.y) });
  }

  /** La nave ya está en coordenadas del predio: no hay origen que trasladar. */
  function alBajarNave(e: React.PointerEvent, n: NavePlanta) {
    setSeccion('Naves');
    const p = aMetros(e);
    if (!p) return;
    e.currentTarget.setPointerCapture(e.pointerId);
    setSeleccion(null);
    setArrastre({ tipo: 'nave', id: String(n.id), dxM: p.x - n.x, dyM: p.y - n.y });
  }

  /**
   * Los muros del predio, en coordenadas del predio, para enganchar el clic
   * de una abertura: los cuatro lados de cada nave más cada pared interior.
   * Se arma con la posición **guardada**, no con la de un arrastre en curso
   * — mientras se coloca una abertura no se puede arrastrar una nave, así
   * que no hace falta mirar el preview, a diferencia de cómo se dibuja el
   * plano. Cada muro viaja con su ancla —de qué pared o de qué lado es—
   * porque `muroMasCercano` sólo devuelve la clave, y hace falta saber a
   * cuál de los dos apunta para armar el pedido a la API.
   */
  function murosParaEnganchar(): { muro: MuroPlano; ancla: { pared: number } | { nave: number; lado: Lado } }[] {
    const perimetrales = datos!.naves.flatMap((n) =>
      LADOS.map((lado) => {
        const { a, b } = ladoDeNave(n, lado);
        return {
          muro: {
            clave: claveDeLado(n.id, lado),
            a: { x: a.x + n.x, y: a.y + n.y },
            b: { x: b.x + n.x, y: b.y + n.y },
          },
          ancla: { nave: n.id, lado },
        };
      }),
    );
    const interiores = datos!.paredes.flatMap((p) => {
      const nave = datos!.naves.find((n) => n.id === p.nave);
      if (!nave) return [];
      return [
        {
          muro: {
            clave: claveDePared(p.id),
            a: { x: p.x1 + nave.x, y: p.y1 + nave.y },
            b: { x: p.x2 + nave.x, y: p.y2 + nave.y },
          },
          ancla: { pared: p.id },
        },
      ];
    });
    return [...perimetrales, ...interiores];
  }

  /** Coloca una abertura del tipo elegido sobre el muro más cercano al clic. */
  function colocarAbertura(p: { x: number; y: number }) {
    const candidatos = murosParaEnganchar();
    const enganche = muroMasCercano(p, candidatos.map((cand) => cand.muro));
    if (!enganche) {
      setAvisoTexto({ texto: 'Ahí no hay ningún muro cerca. Hacé clic sobre una pared.', ok: false });
      return;
    }
    const ancla = candidatos.find((cand) => cand.muro.clave === enganche.muro.clave)!.ancla;
    const nombreTipo = datos!.tiposDeAbertura.find((t) => t.id === nuevaAbertura.tipo)?.nombre ?? 'Abertura';
    void accion(
      () =>
        api.crearAbertura({
          ...('pared' in ancla ? { pared: ancla.pared } : { nave: ancla.nave, lado: ancla.lado }),
          offset: ajustar(enganche.offsetM),
          tipo: nuevaAbertura.tipo,
          nombre: nuevaAbertura.nombre || undefined,
        }),
      `${nombreTipo} colocado.`,
    );
  }

  function alEmpezarTrazo(e: React.PointerEvent) {
    if (!dibujando) return;
    const p = aMetros(e);
    if (!p) return;
    e.currentTarget.setPointerCapture(e.pointerId);

    // La abertura no se traza: se hace clic y nace ahí mismo, con el ancho
    // por defecto del tipo. No hay arrastre que seguir, así que se resuelve
    // acá y no se llega a tocar el estado del trazo.
    if (dibujando === 'abertura') {
      colocarAbertura(p);
      return;
    }

    setTrazo({ x0: p.x, y0: p.y, x1: p.x, y1: p.y });
  }

  function alMover(e: React.PointerEvent) {
    if (trazo) {
      const p = aMetros(e);
      if (p) setTrazo({ ...trazo, x1: p.x, y1: p.y });
      return;
    }

    if (!arrastre) return;
    const p = aMetros(e);
    if (!p) return;

    if (arrastre.tipo === 'nave') {
      const mia = datos!.naves.find((n) => String(n.id) === arrastre.id)!;
      const otras = datos!.naves.filter((n) => n.id !== mia.id);
      const bordesX = otras.flatMap((n) => [n.x, n.x + n.ancho]);
      const bordesY = otras.flatMap((n) => [n.y, n.y + n.largo]);
      const x = ajustar(p.x - arrastre.dxM);
      const y = ajustar(p.y - arrastre.dyM);

      // El enganche vive en `arrastre.ts` y tiene pruebas: es la única cuenta
      // nueva del editor y ya se equivocó dos veces.
      setPreview({
        tipo: 'nave',
        id: arrastre.id,
        x: engancharTramoABordes(x, mia.ancho, bordesX),
        y: engancharTramoABordes(y, mia.largo, bordesY),
      });
      return;
    }

    setPreview({
      tipo: arrastre.tipo,
      id: arrastre.id,
      x: ajustar(p.x - arrastre.dxM),
      y: ajustar(p.y - arrastre.dyM),
    });
  }

  function alSoltar() {
    // La pared no es un rectángulo: es un segmento, así que usa
    // `ajustarASegmento` en vez de `rectanguloDelTrazo` y se resuelve aparte,
    // antes de que el resto del código la trate como una de las otras.
    if (trazo && dibujando === 'pared') {
      const { a, b } = ajustarASegmento({ x: trazo.x0, y: trazo.y0 }, { x: trazo.x1, y: trazo.y1 });
      setTrazo(null);
      if (a.x === b.x && a.y === b.y) {
        setAvisoTexto({ texto: 'La pared quedó muy corta. Marcá un trazo más largo.', ok: false });
        return;
      }
      // Igual que el sector: una pared va adentro de una nave, y si el trazo
      // cae afuera de todas no se guarda. Guardarla igual la dejaría con
      // coordenadas relativas a una nave que no es la que se ve en el trazo.
      const nave = naveEnElPunto(a, datos!.naves);
      if (!nave) {
        setAvisoTexto({ texto: 'Una pared va adentro de una nave. Dibujala sobre una.', ok: false });
        return;
      }
      const local1 = aCoordenadasDeNave(a, nave);
      const local2 = aCoordenadasDeNave(b, nave);
      const largoM = Math.hypot(b.x - a.x, b.y - a.y);
      void accion(
        () =>
          api.crearPared({
            nave: nave.id,
            x1: local1.x,
            y1: local1.y,
            x2: local2.x,
            y2: local2.y,
            material: nuevaPared.material || undefined,
            alto: nuevaPared.alto === '' ? null : Number(nuevaPared.alto),
          }),
        `Pared dibujada: ${largoM} m.`,
      );
      return;
    }

    if (trazo) {
      const r = rectanguloDelTrazo(trazo);
      setTrazo(null);
      if (r.ancho < PASO_M || r.largo < PASO_M) {
        // El aviso es de cualquiera de los tres dibujos, no sólo del camino.
        setAvisoTexto({ texto: 'El rectángulo quedó muy chico. Marcá uno más grande.', ok: false });
        return;
      }
      // El rack sale del trazo entero: el lado largo dice para dónde corre y
      // cuántas posiciones entran, y el grupo se despliega desde ahí. Va antes
      // que la nave porque, a diferencia del resto, no se guarda contra la
      // nave donde cayó sino contra la del sector que se eligió.
      if (dibujando === 'rack') {
        const sector = datos!.sectores.find((s) => s.id === nuevoRack.sector);
        if (!sector) {
          setAvisoTexto({ texto: 'Elegí el sector al que va el rack.', ok: false });
          return;
        }
        const suNave = datos!.naves.find((n) => n.id === sector.nave)!;
        // Dibujarlo sobre otra nave guardaría coordenadas de la nave del
        // sector: el rack aparecería lejos del trazo, sin que nadie lo note.
        const donde = naveEnElPunto({ x: r.x, y: r.y }, datos!.naves);
        if (!donde || donde.id !== suNave.id) {
          setAvisoTexto({
            texto: `El sector ${sector.id} está en «${suNave.nombre}». Dibujá el rack sobre esa nave.`,
            ok: false,
          });
          return;
        }
        const vertical = r.largo > r.ancho;
        const local = aCoordenadasDeNave({ x: r.x, y: r.y }, suNave);
        const cantidad = Math.max(1, Number(nuevoRack.cantidad) || 1);
        const unLugar = nuevoRack.tipo !== 'rack';
        void accion(
          () =>
            api.crearRack({
              sector: sector.id,
              numero: Number(nuevoRack.numero),
              // Dibujado, el largo del trazo decide las columnas; la estación
              // y la zona de piso las fuerzan a 1 igual que el servidor.
              columnas: unLugar ? 1 : Number(nuevoRack.columnas),
              niveles: unLugar ? 1 : Number(nuevoRack.niveles),
              x: local.x,
              y: local.y,
              angulo: vertical ? 90 : 0,
              largo: vertical ? r.largo : r.ancho,
              cantidad,
              separacion: Number(nuevoRack.separacion) || 0,
              tipo: nuevoRack.tipo,
            }),
          cantidad === 1 ? 'Rack dibujado.' : `${cantidad} racks dibujados.`,
          // El número sigue donde terminó el grupo, así el trazo siguiente no
          // choca con lo que se acaba de crear.
          () => setNuevoRack((k) => ({ ...k, numero: String(Number(k.numero) + cantidad) })),
        );
        return;
      }

      // La nave se dibuja sobre el predio: su rectángulo ya está en las
      // coordenadas que se guardan, sin traducción de por medio.
      if (dibujando === 'nave') {
        void accion(
          () =>
            api.crearNave({
              nombre: nuevaNave.nombre,
              x: r.x,
              y: r.y,
              ancho: r.ancho,
              largo: r.largo,
              alto: Number(nuevaNave.alto),
            }),
          `Nave dibujada: ${r.ancho} × ${r.largo} m.`,
          () => setNuevaNave((n) => ({ ...n, nombre: '' })),
        );
        return;
      }

      // Todo lo demás vive adentro de una nave: se resuelve en cuál cayó el
      // trazo y se manda relativo a ella, que es como se guarda.
      const nave = naveEnElPunto({ x: r.x, y: r.y }, datos!.naves);
      if (dibujando === 'sector' && !nave) {
        setAvisoTexto({ texto: 'Un sector va adentro de una nave. Dibujalo sobre una.', ok: false });
        return;
      }
      const local = nave ? aCoordenadasDeNave({ x: r.x, y: r.y }, nave) : { x: r.x, y: r.y };

      if (dibujando === 'sector') {
        const id = nuevoSector.id.trim().toUpperCase();
        void accion(
          () =>
            api.crearSector({
              id,
              nombre: nuevoSector.nombre,
              uso: nuevoSector.uso,
              x: local.x,
              y: local.y,
              ancho: r.ancho,
              largo: r.largo,
              nave: nave!.id,
            }),
          `Sector dibujado: ${r.ancho} × ${r.largo} m.`,
          () => setNuevoSector((s) => ({ ...s, id: '', nombre: '', uso: '' })),
        );
        return;
      }

      const nombre = nuevoCamino.nombre.trim() || `Camino ${(datos?.caminos.length ?? 0) + 1}`;
      void accion(
        () =>
          api.crearCamino({
            nombre,
            tipo: nuevoCamino.tipo,
            x: local.x,
            y: local.y,
            ancho: r.ancho,
            largo: r.largo,
            nave: nave?.id ?? null,
          }),
        `«${nombre}» dibujado: ${r.ancho} × ${r.largo} m.`,
        () => setNuevoCamino((k) => ({ ...k, nombre: '' })),
      );
      return;
    }

    if (arrastre && preview) {
      const destino = preview;

      // Un sector o un muelle soltado fuera de toda nave no se guarda: se
      // avisa, igual que al dibujar un sector. Guardarlo sería peor que
      // rechazarlo, porque las coordenadas del predio se archivarían como si
      // fueran de su nave vieja y la cosa reaparecería lejos del dedo, sin que
      // el backend lo note: él la valida contra esa misma nave vieja.
      if ((destino.tipo === 'sector' || destino.tipo === 'muelle') &&
          !naveEnElPunto({ x: destino.x, y: destino.y }, datos!.naves)) {
        setAvisoTexto({
          texto:
            destino.tipo === 'sector'
              ? 'Un sector va adentro de una nave. Soltalo sobre una.'
              : 'Un muelle va sobre la pared de una nave. Soltalo sobre una.',
          ok: false,
        });
        setArrastre(null);
        setPreview(null);
        return;
      }

      // A qué nave cayó el destino, calculado una sola vez: lo usan tanto los
      // avisos de acá abajo como el pedido que arma `accion`.
      const naveDestino = naveEnElPunto({ x: destino.x, y: destino.y }, datos!.naves);

      // Un sector no arrastra sus racks con él: guardan coordenadas LOCALES
      // de la nave del sector, así que mudar el sector a otra nave no las
      // toca, y el rincón nuevo las corre por el predio -sin que `racksFuera`
      // lo note, porque compara la huella local contra las medidas de la
      // nave nueva, no contra dónde terminan cayendo-. Antes que
      // teletransportarlos en silencio, se impide el arrastre entre naves,
      // igual que ya se impide soltar el sector afuera de todas.
      if (destino.tipo === 'sector') {
        const sector = datos!.sectores.find((s) => s.id === destino.id)!;
        if (naveDestino && naveDestino.id !== sector.nave) {
          setAvisoTexto({
            texto:
              'Un sector no se puede arrastrar a otra nave: sus racks quedarían fuera de lugar. ' +
              'Movelo dentro de su nave, o mudá los racks uno por uno.',
            ok: false,
          });
          setArrastre(null);
          setPreview(null);
          return;
        }
      }

      // Un muelle asignado a un portón sólo tiene sentido en la nave de ese
      // portón -el backend rechaza mandarlo a otra nave sin desasignarlo
      // antes-, y ningún formulario escribe `abertura`. Si el arrastre le
      // cambia la nave, se lo desasigna acá mismo y se avisa, para que el
      // operario sepa que tiene que elegirle uno nuevo en vez de verlo volver
      // solo con un 400 sobre una abertura que nunca vio en el editor.
      let avisoMuelle: string | undefined;
      if (destino.tipo === 'muelle') {
        const muelle = datos!.muelles.find((m) => m.id === destino.id)!;
        if (naveDestino && naveDestino.id !== muelle.nave && muelle.abertura !== null) {
          avisoMuelle = `Muelle ${muelle.id} movido de nave: se le sacó el portón asignado. Asignale uno nuevo.`;
        }
      }

      // La vuelta atrás se arma con lo guardado **antes** de soltar, con la
      // misma API que guarda la posición. El muelle que perdió su portón por
      // cambiar de nave lo recupera: volver a su nave sin él sería deshacer
      // a medias.
      const deshacer = ((): (() => Promise<unknown>) => {
        const id = destino.id;
        if (destino.tipo === 'nave') {
          const n = datos!.naves.find((v) => String(v.id) === id)!;
          return () => api.cambiarNave(n.id, { x: n.x, y: n.y });
        }
        if (destino.tipo === 'rack') {
          const r = datos!.racks.find((v) => v.id === id)!;
          return () => api.cambiarRack(r.id, { x: r.x, y: r.y });
        }
        if (destino.tipo === 'muelle') {
          const m = datos!.muelles.find((v) => v.id === id)!;
          const perdioPorton = avisoMuelle !== undefined;
          return () =>
            api.guardarMuelle(m.id, { x: m.x, y: m.y, nave: m.nave, ...(perdioPorton ? { abertura: m.abertura } : {}) });
        }
        if (destino.tipo === 'camino') {
          const k = datos!.caminos.find((v) => String(v.id) === id)!;
          return () => api.guardarCamino(k.id, { x: k.x, y: k.y, nave: k.nave });
        }
        const sector = datos!.sectores.find((v) => v.id === id)!;
        return () => api.cambiarSector(sector.id, { x: sector.area!.x, y: sector.area!.y });
      })();

      void accion(() => {
        // La nave se guarda tal cual: su posición es la del predio.
        if (destino.tipo === 'nave') return api.cambiarNave(Number(destino.id), { x: destino.x, y: destino.y });

        // El rack no lleva nave: se mueve dentro de la de su sector, así que
        // no se pregunta dónde cayó sino de quién es.
        if (destino.tipo === 'rack') {
          const rack = datos!.racks.find((r) => r.id === destino.id)!;
          const suNave = datos!.naves.find(
            (n) => n.id === datos!.sectores.find((s) => s.id === rack.sector)!.nave,
          )!;
          const p = aCoordenadasDeNave(destino, suNave);
          return api.cambiarRack(destino.id, { x: p.x, y: p.y });
        }

        // Acá pasa la única traducción de vuelta: el preview es del predio y
        // lo que se guarda es relativo a la nave donde se lo soltó. Si cayó en
        // otra, se muda.
        const nave = naveDestino;
        const local = nave ? aCoordenadasDeNave(destino, nave) : destino;
        if (destino.tipo === 'muelle') {
          const muelle = datos!.muelles.find((m) => m.id === destino.id)!;
          const cambiaDeNave = nave !== null && nave.id !== muelle.nave;
          return api.guardarMuelle(destino.id, {
            x: local.x,
            y: local.y,
            ...(nave ? { nave: nave.id } : {}),
            // Ver el aviso de arriba: si cambia de nave, el portón que tenía
            // asignado -si tenía uno- deja de ser de la misma nave.
            ...(cambiaDeNave ? { abertura: null } : {}),
          });
        }
        if (destino.tipo === 'camino')
          return api.guardarCamino(Number(destino.id), { x: local.x, y: local.y, nave: nave?.id ?? null });
        return api.cambiarSector(destino.id, { x: local.x, y: local.y, ...(nave ? { nave: nave.id } : {}) });
      }, avisoMuelle ?? 'Posición guardada.', undefined, deshacer);
    }
    setArrastre(null);
    setPreview(null);
  }

  /**
   * Suelta un rack sin ubicar en el centro de su nave. Las coordenadas que se
   * guardan son de la nave, así que se centra contra sus medidas, no contra
   * las del predio.
   */
  function ubicar(r: RackPlanta) {
    if (!datos) return;
    const suya = medidasDondeVa(naveDeRack(r.id));
    void accion(
      () =>
        api.cambiarRack(r.id, {
          x: ajustar(Math.max(0, suya.ancho / 2 - r.largo / 2)),
          y: ajustar(Math.max(0, suya.largo / 2 - r.profundidad / 2)),
        }),
      `${r.id} quedó en el centro de la nave. Arrastralo a su lugar.`,
    );
  }

  /**
   * Imprime las etiquetas de ubicación de un rack: una por posición y nivel, o
   * la única de la estación o de la zona de piso. El dibujo sale del mismo generador que ya tiene su
   * prueba de fuego contra el escáner (`lib/etiquetasUbicacion.ts`); acá sólo se
   * arman las etiquetas del rack y se las manda a `imprimirEtiquetas`.
   */
  function imprimirUbicaciones(r: RackPlanta) {
    const etiquetas = codigosDeUbicacion(r);
    const problema = imprimirEtiquetas(
      { nombre: `Ubicaciones de ${r.id}`, ancho: 70, alto: 30 },
      etiquetas.map((e) => svgEtiquetaUbicacion(e)),
    );
    if (problema) setAvisoTexto({ texto: problema, ok: false });
  }

  /** Suelta un muelle sin ubicar sobre la pared de entrada de su nave. */
  function ubicarMuelle(m: MuellePlanta) {
    if (!datos) return;
    const suya = medidasDondeVa(m.nave);
    void accion(
      () =>
        api.guardarMuelle(m.id, {
          x: ajustar(Math.max(0, suya.ancho / 2 - m.ancho / 2)),
          y: 0,
        }),
      `${m.id} quedó sobre la pared de arriba. Arrastralo a su lugar.`,
    );
  }

  /** Suelta un camino sin ubicar en el centro de su nave, o del predio si no tiene. */
  function ubicarCamino(k: CaminoPlanta) {
    if (!datos) return;
    const suya = medidasDondeVa(k.nave);
    void accion(
      () =>
        api.guardarCamino(k.id, {
          x: ajustar(Math.max(0, suya.ancho / 2 - k.ancho / 2)),
          y: ajustar(Math.max(0, suya.largo / 2 - k.largo / 2)),
        }),
      `${k.nombre} quedó en el centro de ${k.nave === null ? 'el predio' : 'la nave'}. Arrastralo a su lugar.`,
    );
  }

  if (error) return <PanelError mensaje={error} onReintentar={recargar} />;
  if (cargando && !datos) return <Cargando texto="Leyendo la planta…" />;
  if (!datos) return null;

  const sinUbicar = datos.racks.filter((r) => !r.ubicado);
  const ubicados = datos.racks.filter((r) => r.ubicado);
  const sel = datos.racks.find((r) => r.id === seleccion) ?? null;
  /** El primer color de la paleta que no esté usado: el que le va a tocar. */
  const colorQueSigue = () => {
    const usados = datos.sectores.map((s) => s.color);
    return colorSector(datos.coloresDeSector.find((k) => !usados.includes(k)), datos.sectores.length);
  };

  /** El color que tiene guardado el sector, o uno por su orden si no tiene. */
  const colorDe = (id: string) => {
    const i = datos.sectores.findIndex((s) => s.id === id);
    return colorSector(datos.sectores[i]?.color, Math.max(i, 0));
  };

  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: 20 }}>
      <Encabezado
        bajada="Dibujá la nave a escala y acomodá los racks. De acá salen las ubicaciones y el mapa."
      />

      <div id="wms-mapa-grid" style={{ display: 'grid', gridTemplateColumns: 'minmax(0,1.6fr) minmax(300px,1fr)', gap: 16, alignItems: 'start' }}>
        {/* ------------------------------------------------------- el plano */}
        <div id="wms-planta-plano" style={{ ...tarjeta, padding: 16, minWidth: 0 }}>
          <PlanoEditable
            datos={datos}
            svgRef={svg}
            seleccion={seleccion}
            preview={preview}
            colorDe={colorDe}
            onBajar={alBajar}
            onBajarMuelle={alBajarMuelle}
            onBajarCamino={alBajarCamino}
            onBajarSector={alBajarSector}
            onBajarNave={alBajarNave}
            dibujando={dibujando}
            trazo={trazo && dibujando !== 'pared' ? rectanguloDelTrazo(trazo) : null}
            trazoPared={
              trazo && dibujando === 'pared'
                ? ajustarASegmento({ x: trazo.x0, y: trazo.y0 }, { x: trazo.x1, y: trazo.y1 })
                : null
            }
            colorTrazo={
              dibujando === 'pared' || dibujando === 'abertura'
                ? // La pared se dibuja como segmento aparte (`trazoPared`), y la
                  // abertura no tiene preview de trazo -nace de un clic-: este
                  // color no se usa en ninguna de las dos, pero el prop sigue
                  // ahí para los demás.
                  undefined
                : dibujando === 'rack'
                  ? // El rack se pinta del color de su sector, así que el trazo
                    // ya se ve como va a quedar.
                    { relleno: colorDe(nuevoRack.sector), borde: colorDe(nuevoRack.sector) }
                  : dibujando === 'sector'
                    ? { relleno: colorQueSigue(), borde: colorQueSigue() }
                    : dibujando === 'nave'
                      ? // La nave se dibuja como se ve: piso blanco con borde firme.
                        { relleno: '#fff', borde: c.bordeFuerte }
                      : colorCamino(nuevoCamino.tipo)
            }
            onEmpezarTrazo={alEmpezarTrazo}
            onMover={alMover}
            onSoltar={alSoltar}
          />
          <div style={{ marginTop: 12, fontSize: 12.5, color: c.texto2, display: 'flex', gap: 16, flexWrap: 'wrap' }}>
            <span>
              {dibujando === 'abertura'
                ? 'Hacé clic sobre un muro para poner la abertura ahí'
                : dibujando
                  ? `Marcá el ${dibujando === 'pared' ? 'trazo' : 'rectángulo'} ${QUE_SE_DIBUJA[dibujando]} sobre el plano · se ajusta cada ${PASO_M} m`
                  : `Arrastrá un rack o una nave para moverla · se ajusta cada ${PASO_M} m`}
            </span>
            <span style={{ flex: 1, minWidth: 0, textAlign: 'right', color: c.texto3 }}>
              Predio {datos.predio.ancho} × {datos.predio.largo} m ·{' '}
              {datos.naves.length === 1 ? '1 nave' : `${datos.naves.length} naves`}
            </span>
          </div>
        </div>

        {/* ---------------------------------------------------- panel lateral */}
        <div style={{ display: 'flex', flexDirection: 'column', gap: 16, minWidth: 0 }}>
          {/* Con `key` en el rack: elegir otro en el plano vuelve a hacer
              entrar el panel, y se nota que los campos son de otro rack. Nace
              del lado que da al plano (en la PC el borde izquierdo; en el
              celular, que va abajo, el de arriba) y al cerrarlo vuelven las
              secciones. */}
          <AnimatePresence mode="wait" initial={false}>
          {sel ? (
            <motion.div key={sel.id} {...(celular ? revelarDesde('50%', '0px') : revelarDesde('0px', '28px'))}>
            <PanelRack
              rack={sel}
              onCambiar={(cambio) => accion(() => api.cambiarRack(sel.id, cambio), `Rack ${sel.id} guardado.`)}
              onImprimir={() => imprimirUbicaciones(sel)}
              onBorrar={() =>
                borrar(
                  {
                    titulo: `¿Borrar el rack ${sel.id}?`,
                    detalle: `Se borran sus ${sel.lugares} posiciones.`,
                    boton: `Borrar el rack y sus ${sel.lugares} posiciones`,
                  },
                  () => api.borrarRack(sel.id),
                  `Se borró el rack ${sel.id}.`,
                  () => setSeleccion(null),
                )
              }
              onCerrar={() => setSeleccion(null)}
            />
            </motion.div>
          ) : (
            <motion.div key="secciones" {...aparecer} style={{ display: 'flex', flexDirection: 'column', gap: 16 }}>
            <Plegable
              abierta={seccion}
              cuentas={{
                Naves: datos.naves.length,
                Paredes: datos.paredes.length,
                Aberturas: datos.aberturas.length,
                Sectores: datos.sectores.length,
                'Agregar rack': datos.racks.length,
                Muelles: datos.muelles.length,
                'Caminos y zonas': datos.caminos.length,
              }}
              // Abrir una cierra la que estaba; tocar la abierta la cierra.
              onAlternar={(titulo) => setSeccion((s) => (s === titulo ? null : titulo))}
            >
              <FormularioPredio
                valores={predio}
                onCambiar={setPredio}
                onGuardar={() =>
                  accion(() => api.guardarPredio({ nombre: predio.nombre }), 'Nombre del predio guardado.')
                }
              />

              <FormularioNave
                naves={datos.naves}
                valores={nuevaNave}
                fallas={fallas}
                onCambiar={setNuevaNave}
                onBorrar={(n) =>
                  borrar(
                    { titulo: `¿Borrar ${n.nombre}?`, detalle: 'Está vacía: sólo se va su contorno del plano.', boton: 'Borrar la nave' },
                    () => api.borrarNave(n.id),
                    `${n.nombre} borrada.`,
                  )
                }
                onRenombrar={(id, nombre) => accion(() => api.cambiarNave(id, { nombre }), 'Nombre guardado.')}
                onMedidas={(id, m) =>
                  accion(
                    () => api.cambiarNave(id, m),
                    `Medidas de ${datos.naves.find((n) => n.id === id)?.nombre ?? 'la nave'} guardadas.`,
                  )
                }
                dibujando={dibujando === 'nave'}
                onDibujar={() => setDibujando((d) => (d === 'nave' ? null : 'nave'))}
              />

              <FormularioPared
                valores={nuevaPared}
                paredes={datos.paredes}
                naves={datos.naves}
                materiales={datos.materialesDePared}
                fallas={fallas}
                onCambiar={setNuevaPared}
                onLargo={(p, largo) => {
                  // Estirarla es mover el segundo punto sobre el eje de la
                  // pared, sin tocar el primero. `ajustarASegmento` la deja
                  // horizontal o vertical al trazarla, pero una pared puede
                  // llegar en diagonal por la API o por el seed, así que acá
                  // se usa su vector unitario en vez de asumir un eje: en el
                  // caso ortogonal da exactamente lo mismo que antes.
                  const dx = p.x2 - p.x1;
                  const dy = p.y2 - p.y1;
                  const largoActual = Math.hypot(dx, dy) || 1;
                  const ux = dx / largoActual;
                  const uy = dy / largoActual;
                  void accion(
                    () => api.cambiarPared(p.id, { x2: p.x1 + ux * largo, y2: p.y1 + uy * largo }),
                    'Largo de la pared guardado.',
                  );
                }}
                onMaterial={(id, material) => accion(() => api.cambiarPared(id, { material }), 'Material guardado.')}
                onAlto={(id, alto) => accion(() => api.cambiarPared(id, { alto }), 'Alto de la pared guardado.')}
                onBorrar={(id) => {
                  // La base se lleva sus aberturas en cascada.
                  const aberturas = datos.aberturas.filter((a) => a.pared === id).length;
                  void borrar(
                    aberturas
                      ? {
                          titulo: '¿Borrar esta pared?',
                          detalle: `Se borran también ${aberturas === 1 ? 'su abertura' : `sus ${aberturas} aberturas`}.`,
                          boton: aberturas === 1 ? 'Borrar la pared y su abertura' : `Borrar la pared y sus ${aberturas} aberturas`,
                        }
                      : { titulo: '¿Borrar esta pared?', boton: 'Borrar la pared' },
                    () => api.borrarPared(id),
                    'Se borró la pared.',
                  );
                }}
                dibujando={dibujando === 'pared'}
                onDibujar={() => setDibujando((d) => (d === 'pared' ? null : 'pared'))}
              />

              <FormularioAbertura
                valores={nuevaAbertura}
                aberturas={datos.aberturas}
                naves={datos.naves}
                paredes={datos.paredes}
                tipos={datos.tiposDeAbertura}
                fallas={fallas}
                onCambiar={setNuevaAbertura}
                onEditar={(id, cambio) => accion(() => api.cambiarAbertura(id, cambio), 'Abertura guardada.')}
                onBorrar={(id) =>
                  borrar(
                    { titulo: '¿Borrar esta abertura?', boton: 'Borrar la abertura' },
                    () => api.borrarAbertura(id),
                    'Se borró la abertura.',
                  )
                }
                dibujando={dibujando === 'abertura'}
                onDibujar={() => setDibujando((d) => (d === 'abertura' ? null : 'abertura'))}
              />

              <FormularioSector
                valores={nuevoSector}
                sectores={datos.sectores}
                naves={datos.naves}
                colores={datos.coloresDeSector}
                colorDe={colorDe}
                onCambiar={setNuevoSector}
                onCrear={() =>
                  accion(
                    () =>
                      api.crearSector({
                        id: nuevoSector.id,
                        nombre: nuevoSector.nombre,
                        uso: nuevoSector.uso,
                        ...(nuevoSector.nave ? { nave: Number(nuevoSector.nave) } : {}),
                      }),
                    `Sector ${nuevoSector.id.toUpperCase()} creado.`,
                    () => setNuevoSector((s) => ({ ...s, id: '', nombre: '', uso: '' })),
                  )
                }
                onBorrar={(id) => {
                  const racks = datos.racks.filter((r) => r.sector === id);
                  const posiciones = racks.reduce((a, r) => a + r.lugares, 0);
                  const suyos = racks.length === 1 ? 'su rack' : `sus ${racks.length} racks`;
                  void borrar(
                    racks.length
                      ? {
                          titulo: `¿Borrar el sector ${id}?`,
                          detalle: `Se borran ${suyos} y ${posiciones} posiciones.`,
                          boton: `Borrar el sector y ${suyos}`,
                        }
                      : { titulo: `¿Borrar el sector ${id}?`, detalle: 'No tiene racks.', boton: 'Borrar el sector' },
                    () => api.borrarSector(id),
                    `Sector ${id} borrado.`,
                  );
                }}
                onColor={(id, color) => accion(() => api.cambiarSector(id, { color }), 'Color guardado.')}
                onRenombrar={(id, nuevo) =>
                  accion(
                    () => api.renombrarSector(id, nuevo),
                    `El sector ${id} ahora es ${nuevo}: sus racks y posiciones se renombraron solos.`,
                  )
                }
                dibujando={dibujando === 'sector'}
                onDibujar={() => setDibujando((d) => (d === 'sector' ? null : 'sector'))}
              />

              <FormularioRack
                valores={nuevoRack}
                sectores={datos.sectores}
                onCambiar={setNuevoRack}
                onCrear={() =>
                  accion(
                    () =>
                      api.crearRack({
                        sector: nuevoRack.sector,
                        numero: Number(nuevoRack.numero),
                        columnas: nuevoRack.tipo !== 'rack' ? 1 : Number(nuevoRack.columnas),
                        niveles: nuevoRack.tipo !== 'rack' ? 1 : Number(nuevoRack.niveles),
                        cantidad: Math.max(1, Number(nuevoRack.cantidad) || 1),
                        tipo: nuevoRack.tipo,
                      }),
                    Number(nuevoRack.cantidad) > 1
                      ? `${nuevoRack.cantidad} racks creados. Están en «sin ubicar»: ponelos en el plano.`
                      : 'Rack creado. Está en «sin ubicar»: ponelo en el plano.',
                    () =>
                      setNuevoRack((r) => ({
                        ...r,
                        numero: String(Number(r.numero) + Math.max(1, Number(r.cantidad) || 1)),
                      })),
                  )
                }
                dibujando={dibujando === 'rack'}
                onDibujar={() => setDibujando((d) => (d === 'rack' ? null : 'rack'))}
              />

              <FormularioMuelle
                valores={nuevoMuelle}
                muelles={datos.muelles}
                naves={datos.naves}
                onCambiar={setNuevoMuelle}
                onCrear={() =>
                  accion(
                    () =>
                      api.crearMuelle({
                        id: nuevoMuelle.id,
                        ancho: Number(nuevoMuelle.ancho),
                        largo: Number(nuevoMuelle.largo),
                        ...(nuevoMuelle.nave ? { nave: Number(nuevoMuelle.nave) } : {}),
                      }),
                    `Muelle ${nuevoMuelle.id.toUpperCase()} creado. Ponelo sobre la pared que corresponda.`,
                    () => setNuevoMuelle((m) => ({ ...m, id: '' })),
                  )
                }
                onBorrar={(id) =>
                  borrar(
                    { titulo: `¿Borrar el muelle ${id}?`, boton: 'Borrar el muelle' },
                    () => api.borrarMuelle(id),
                    `Muelle ${id} borrado.`,
                  )
                }
                onUbicar={ubicarMuelle}
              />

              <FormularioCamino
                valores={nuevoCamino}
                caminos={datos.caminos}
                naves={datos.naves}
                tipos={datos.tiposDeCamino}
                onCambiar={setNuevoCamino}
                onCrear={() =>
                  accion(
                    () =>
                      api.crearCamino({
                        nombre: nuevoCamino.nombre,
                        tipo: nuevoCamino.tipo,
                        ancho: Number(nuevoCamino.ancho),
                        largo: Number(nuevoCamino.largo),
                        // «En el predio» es la playa de maniobras: sin nave.
                        // El '' —sin inicializar, que sólo se daría en un
                        // predio sin naves— también va sin nave, antes que
                        // mandar el 0 que devolvería `Number('')`.
                        nave:
                          nuevoCamino.nave && nuevoCamino.nave !== EN_EL_PREDIO
                            ? Number(nuevoCamino.nave)
                            : null,
                      }),
                    `«${nuevoCamino.nombre}» creado. Ponelo donde va.`,
                    () => setNuevoCamino((k) => ({ ...k, nombre: '' })),
                  )
                }
                onBorrar={(id, nombre) =>
                  borrar(
                    { titulo: `¿Borrar «${nombre}»?`, boton: 'Borrar el camino' },
                    () => api.borrarCamino(id),
                    `«${nombre}» borrado.`,
                  )
                }
                onUbicar={ubicarCamino}
                onRenombrar={(id, nombre) => accion(() => api.guardarCamino(id, { nombre }), 'Nombre guardado.')}
                dibujando={dibujando === 'camino'}
                onDibujar={() => setDibujando((d) => (d === 'camino' ? null : 'camino'))}
              />
            </Plegable>
            </motion.div>
          )}
          </AnimatePresence>

          <AnimatePresence initial={false}>
          {sinUbicar.length > 0 && (
            <motion.div key="sin-ubicar" {...desplegar}>
            <div style={{ ...tarjeta, padding: 16, display: 'flex', flexDirection: 'column', gap: 10 }}>
              <span style={{ fontSize: 15, fontWeight: 600, color: c.texto }}>
                Sin ubicar · <Numero valor={sinUbicar.length} />
              </span>
              <span style={{ fontSize: 12.5, color: c.texto2, textWrap: 'pretty' }}>
                Estos racks existen y tienen posiciones, pero todavía no están en el plano.
              </span>
              {/* El que se ubica se va achicándose y los demás se corren a
                  ocupar su lugar. `position: relative` para el `popLayout`. */}
              <div style={{ display: 'flex', flexWrap: 'wrap', gap: 8, position: 'relative' }}>
                <AnimatePresence mode="popLayout" initial={false}>
                {sinUbicar.map((r, i) => (
                  <motion.button
                    key={r.id}
                    {...fila(i)}
                    exit={pop.exit}
                    layout
                    transition={{ ...fila(i).transition, layout: resorte }}
                    type="button"
                    onClick={() => ubicar(r)}
                    title={`Poner ${r.id} en el plano`}
                    style={{
                      display: 'inline-flex',
                      alignItems: 'center',
                      gap: 6,
                      padding: '7px 12px',
                      borderRadius: 999,
                      border: `1px dashed ${c.bordeFuerte}`,
                      background: c.fondo,
                      fontFamily: mono,
                      fontSize: 12.5,
                      color: c.texto,
                      cursor: 'pointer',
                    }}
                  >
                    <Icono nombre="add_location_alt" size={16} color={c.texto3} />
                    {r.id}
                    {r.tipo === 'estacion' && <span style={chip('neu')}>Estación</span>}
                    {r.tipo === 'piso' && <span style={chip('neu')}>Zona de piso</span>}
                  </motion.button>
                ))}
                </AnimatePresence>
              </div>
            </div>
            </motion.div>
          )}
          </AnimatePresence>

          <div style={nota}>
            <Numero valor={ubicados.length} /> de <Numero valor={datos.racks.length} /> racks ubicados ·{' '}
            <Numero valor={datos.racks.reduce((a, r) => a + r.lugares, 0)} /> lugares de pallet en total.
          </div>
        </div>
      </div>
      {/* El centrado va por `x` de Motion y no por `transform`: un transform
          escrito a mano lo pisaría la animación de entrada, y el aviso
          aparecería corrido a la derecha. */}
      <AnimatePresence>
      {avisoTexto && (
        <motion.div
          key={avisoTexto.texto}
          {...aparecer}
          role={avisoTexto.ok ? 'status' : 'alert'}
          style={{
            ...aviso(avisoTexto.ok ? 'ok' : 'err'),
            position: 'fixed',
            bottom: 24,
            left: '50%',
            x: '-50%',
            zIndex: 50,
            width: 'max-content',
            maxWidth: 'min(560px, calc(100vw - 32px))',
            display: 'flex',
            alignItems: 'center',
            gap: 12,
            boxShadow: '0 8px 24px rgba(0,0,0,.15)',
          }}
        >
          <span style={{ flex: 1, minWidth: 0 }}>{avisoTexto.texto}</span>
          {avisoTexto.deshacer && (
            <button
              type="button"
              onClick={() => void accion(avisoTexto.deshacer!, 'Volvió a donde estaba.')}
              style={{ ...botonSecundario, minHeight: altoMinimo(34), padding: '0 12px' }}
            >
              Deshacer
            </button>
          )}
          <button
            type="button"
            onClick={() => setAvisoTexto(null)}
            aria-label="Cerrar el aviso"
            style={{ border: 'none', background: 'none', cursor: 'pointer', display: 'grid', placeItems: 'center' }}
          >
            <Icono nombre="close" size={18} color={c.texto3} />
          </button>
        </motion.div>
      )}
      </AnimatePresence>
      {dialogo}
    </div>
  );
}
